import { pantryColors } from '@pantry/ui'
import React, { useMemo, useState } from 'react'
import type {
  LoyaltyCampaign,
  LoyaltyFlow,
} from '../../../../types/loyaltyMarketing'
import { campaignChannel, campaignType, flowListType } from '../campaignDisplay'
import {
  EMERALD,
  EMERALD_BG,
  CAMPAIGN_TYPE_TINTS,
  flowVisual,
} from './CampaignTypeIcon'
import { PULSE_ANIMATION, PulseStyle } from './StatusPill'

const p = pantryColors

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_CHIPS_PER_DAY = 2

// A campaign lands on the calendar at its send date (sent) or scheduled date.
// Drafts have neither and are excluded (surfaced via the note above the grid).
const campaignDate = (campaign: LoyaltyCampaign): Date | null => {
  const raw = campaign.sent_at || campaign.scheduled_at
  if (!raw) return null
  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

// Mock `campaignRange`: [start, end] calendar days. `ends_at` (optional
// promotional window end) extends the chip across days; without it a campaign
// is a single-day chip.
const campaignRange = (
  campaign: LoyaltyCampaign,
): { start: Date; end: Date } | null => {
  const start = campaignDate(campaign)
  if (!start) return null
  const rawEnd = campaign.ends_at ? new Date(campaign.ends_at) : null
  const end =
    rawEnd && !Number.isNaN(rawEnd.getTime()) && rawEnd > start ? rawEnd : start
  return { start, end }
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

// One chip placement: the campaign plus whether it continues past this cell
// on either side (mock `.chip-continues-before` / `-after` arrows).
interface DayEntry {
  campaign: LoyaltyCampaign
  continuesBefore: boolean
  continuesAfter: boolean
}

const dayKey = (year: number, month: number, day: number) =>
  `${year}-${month}-${day}`

// Chip tints by type per the mock's `.camp-chip.{email,sms,cart}`: email =
// blue, sms = teal, abandoned cart = orange; failed sends get a red tint and
// drafts a gray dashed look.
const chipTint = (campaign: LoyaltyCampaign): { fg: string; bg: string } => {
  if (campaign.status === 'failed') return { fg: p.red[700], bg: p.red[50] }
  if (campaign.status === 'draft') return { fg: p.gray[400], bg: p.gray[100] }
  if (campaignChannel(campaign) === 'sms')
    return { fg: p.teal[700], bg: p.teal[50] }
  const type = campaignType(campaign)
  if (type === 'abandoned_cart') return CAMPAIGN_TYPE_TINTS.abandoned_cart
  return { fg: p.blue[700], bg: p.blue[50] }
}

const LegendDot: React.FC<{ color: string; dashed?: boolean }> = ({
  color,
  dashed,
}) => (
  <span
    style={{
      width: 9,
      height: 9,
      borderRadius: 2,
      background: dashed ? p.gray[300] : color,
      border: dashed ? `1px dashed ${p.gray[400]}` : 'none',
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
)

const NavButton: React.FC<{
  label: string
  onClick: () => void
  children: React.ReactNode
}> = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    style={{
      width: 32,
      height: 32,
      border: `1px solid ${p.gray[200]}`,
      borderRadius: 6,
      display: 'grid',
      placeItems: 'center',
      cursor: 'pointer',
      color: p.gray[500],
      background: p.white,
      padding: 0,
    }}
  >
    {children}
  </button>
)

interface CampaignsCalendarProps {
  campaigns: LoyaltyCampaign[]
  flows: LoyaltyFlow[]
  // Chip / automation click → open the detail drawer.
  onOpen?: (kind: 'campaign' | 'flow', id: string) => void
}

// Hand-rolled month grid (PreviewMiniCalendar idiom — no FullCalendar in libs)
// per the reference calendar view: DOW header, prev/next + Today nav, chips at
// each campaign's (sent_at || scheduled_at) date, "Always Running Automations"
// strip of live flows below.
export const CampaignsCalendar: React.FC<CampaignsCalendarProps> = ({
  campaigns,
  flows,
  onOpen,
}) => {
  const today = new Date()
  const [view, setView] = useState<{ year: number; month: number }>(() => ({
    year: today.getFullYear(),
    month: today.getMonth(),
  }))

  const shiftMonth = (delta: number) =>
    setView((prev) => {
      const next = new Date(prev.year, prev.month + delta, 1)
      return { year: next.getFullYear(), month: next.getMonth() }
    })

  const goToday = () =>
    setView({ year: today.getFullYear(), month: today.getMonth() })

  const monthLabel = new Date(view.year, view.month, 1).toLocaleString(
    'en-US',
    { month: 'long', year: 'numeric' },
  )

  const unscheduledDrafts = campaigns.filter(
    (c) => c.status === 'draft' && !campaignDate(c),
  ).length

  // day-key → chip entries for the visible month (drafts excluded). A
  // multi-day campaign (ends_at) is attached to every day of its range that
  // falls in the visible month, flagged with which side(s) it continues on.
  const dayMap = useMemo(() => {
    const map = new Map<string, DayEntry[]>()
    const push = (key: string, entry: DayEntry) => {
      const bucket = map.get(key)
      if (bucket) bucket.push(entry)
      else map.set(key, [entry])
    }
    for (const campaign of campaigns) {
      const range = campaignRange(campaign)
      if (!range) continue
      const cursor = new Date(
        range.start.getFullYear(),
        range.start.getMonth(),
        range.start.getDate(),
      )
      const endDay = new Date(
        range.end.getFullYear(),
        range.end.getMonth(),
        range.end.getDate(),
      )
      // Cap the walk so a bad far-future ends_at can't spin (a year of chips).
      let steps = 0
      while (cursor <= endDay && steps < 366) {
        if (
          cursor.getFullYear() === view.year &&
          cursor.getMonth() === view.month
        ) {
          push(
            dayKey(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()),
            {
              campaign,
              continuesBefore: !sameDay(cursor, range.start),
              continuesAfter: !sameDay(cursor, range.end),
            },
          )
        }
        cursor.setDate(cursor.getDate() + 1)
        steps += 1
      }
    }
    return map
  }, [campaigns, view.year, view.month])

  const firstDow = new Date(view.year, view.month, 1).getDay()
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate()
  const totalCells = Math.ceil((firstDow + daysInMonth) / 7) * 7
  const todayKey = dayKey(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  )

  const liveFlows = flows.filter((flow) => flow.status === 'live')

  return (
    <div style={{ padding: '18px 20px' }}>
      {/* Nav row: month label + Today | legend + prev/next */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: p.gray[800] }}>
            {monthLabel}
          </div>
          <button
            type="button"
            onClick={goToday}
            style={{
              padding: '6px 12px',
              border: `1px solid ${p.gray[200]}`,
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 500,
              color: p.gray[600],
              cursor: 'pointer',
              background: p.white,
            }}
          >
            Today
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {[
              { label: 'Email', node: <LegendDot color={p.blue[700]} /> },
              { label: 'SMS', node: <LegendDot color={p.teal[700]} /> },
              {
                label: 'Abandoned Cart',
                node: <LegendDot color={p.orange[700]} />,
              },
              { label: 'Failed', node: <LegendDot color={p.red[700]} /> },
              {
                label: 'Draft / Scheduled',
                node: <LegendDot color={p.gray[400]} dashed />,
              },
            ].map((item) => (
              <span
                key={item.label}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 11.5,
                  color: p.gray[500],
                }}
              >
                {item.node}
                {item.label}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <NavButton label="Previous month" onClick={() => shiftMonth(-1)}>
              <svg
                width={15}
                height={15}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </NavButton>
            <NavButton label="Next month" onClick={() => shiftMonth(1)}>
              <svg
                width={15}
                height={15}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </NavButton>
          </div>
        </div>
      </div>

      {unscheduledDrafts > 0 && (
        <div style={{ fontSize: 12, color: p.gray[400], marginBottom: 10 }}>
          {unscheduledDrafts} unscheduled{' '}
          {unscheduledDrafts === 1 ? 'draft' : 'drafts'} not shown — schedule
          them to place them on the calendar.
        </div>
      )}

      {/* Month grid — 1px gray gap lines via the container background. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
          background: p.gray[200],
          border: `1px solid ${p.gray[200]}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        {DOW_LABELS.map((label) => (
          <div
            key={label}
            style={{
              background: p.gray[50],
              padding: '7px 0',
              textAlign: 'center',
              fontSize: 11.5,
              fontWeight: 600,
              color: p.gray[500],
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {label}
          </div>
        ))}

        {Array.from({ length: totalCells }).map((_, i) => {
          const cellDate = new Date(view.year, view.month, i - firstDow + 1)
          const outside = cellDate.getMonth() !== view.month
          const key = dayKey(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate(),
          )
          const isToday = key === todayKey
          const dayCampaigns = dayMap.get(key) ?? []
          const shown = dayCampaigns.slice(0, MAX_CHIPS_PER_DAY)
          const extra = dayCampaigns.length - shown.length
          return (
            <div
              key={key}
              style={{
                background: outside
                  ? p.gray[50]
                  : isToday
                    ? '#F0FFFE'
                    : p.white,
                minHeight: 110,
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              <div
                style={
                  isToday
                    ? {
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: p.brand.primary,
                        color: p.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        marginBottom: 2,
                      }
                    : {
                        fontSize: 12.5,
                        fontWeight: 500,
                        color: outside ? p.gray[300] : p.gray[600],
                        marginBottom: 2,
                        lineHeight: 1,
                      }
                }
              >
                {cellDate.getDate()}
              </div>

              {shown.map(({ campaign, continuesBefore, continuesAfter }) => {
                const tint = chipTint(campaign)
                const isScheduled = campaign.status === 'scheduled'
                const isDraft = campaign.status === 'draft'
                return (
                  <div
                    key={campaign.id}
                    title={`${campaign.name || 'Campaign'} — ${campaign.status}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpen?.('campaign', campaign.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onOpen?.('campaign', campaign.id)
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.8'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = isScheduled ? '0.7' : '1'
                    }}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 7px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.3,
                      background: tint.bg,
                      color: tint.fg,
                      border: isDraft
                        ? `1px dashed ${p.gray[300]}`
                        : isScheduled
                          ? `1px dashed ${tint.fg}`
                          : '1px solid transparent',
                      opacity: isScheduled ? 0.7 : 1,
                    }}
                  >
                    {/* Multi-day range arrows (mock .chip-continues-*). */}
                    {continuesBefore && (
                      <span style={{ fontSize: 9, opacity: 0.65 }}>←</span>
                    )}
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: tint.fg,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {campaign.name || 'Campaign'}
                    </span>
                    {continuesAfter && (
                      <span
                        style={{
                          fontSize: 9,
                          opacity: 0.65,
                          marginLeft: 'auto',
                        }}
                      >
                        →
                      </span>
                    )}
                  </div>
                )
              })}

              {extra > 0 && (
                <div
                  style={{
                    fontSize: 10.5,
                    color: p.gray[400],
                    padding: '1px 4px',
                  }}
                >
                  +{extra} more
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Always-running automations (live flows) */}
      {liveFlows.length > 0 && (
        <div
          style={{
            marginTop: 16,
            background: p.white,
            border: `1px solid ${p.gray[200]}`,
            borderRadius: 12,
            padding: '14px 18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 11,
              fontWeight: 700,
              color: p.gray[400],
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 12,
            }}
          >
            <PulseStyle />
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: EMERALD,
                display: 'inline-block',
                animation: PULSE_ANIMATION,
              }}
            />
            Always Running Automations
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {liveFlows.map((flow) => {
              const visual = flowVisual(
                flowListType(flow),
                flow.trigger_metric,
                13,
              )
              return (
                <div
                  key={flow.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpen?.('flow', flow.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onOpen?.('flow', flow.id)
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = p.brand.primary
                    e.currentTarget.style.background = p.brand.light
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = p.gray[200]
                    e.currentTarget.style.background = p.gray[50]
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 14px',
                    border: `1px solid ${p.gray[200]}`,
                    borderRadius: 8,
                    background: p.gray[50],
                    cursor: 'pointer',
                    transition: 'background .12s, border-color .12s',
                  }}
                >
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      display: 'grid',
                      placeItems: 'center',
                      background: visual.tint.bg,
                      color: visual.tint.fg,
                      flexShrink: 0,
                    }}
                  >
                    {visual.icon}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: p.gray[700],
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: EMERALD,
                          flexShrink: 0,
                          animation: PULSE_ANIMATION,
                        }}
                      />
                      {flow.name || 'Flow'}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: p.gray[400],
                      }}
                    >
                      {(flow.stats?.sent ?? 0).toLocaleString()} triggered ·{' '}
                      {(flow.stats?.conversions ?? 0).toLocaleString()}{' '}
                      converted
                    </span>
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: EMERALD,
                      background: EMERALD_BG,
                      padding: '1px 7px',
                      borderRadius: 20,
                    }}
                  >
                    LIVE
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
